// CFG-IA.B1 (#1541) — Settings → GitHub.
//
// ADMIN-ONLY per the #1595 hybrid Member-Settings-Visibility decision —
// integration/operational config (Storage, GitHub) is hidden from members,
// unlike governance config (Deployment/QA/Agents/Agent-Model/Estimation),
// which stays member-read-only. GitHub's write paths were already fully
// admin-gated server-side (connect/callback/disconnect/PAT-save all require
// TENANT_ADMIN — see byoi-github.ts and tenant-config.ts); this is a
// visibility-only change. Fail-CLOSED, modeled on SettingsInfrastructurePage:
// a null/failed membership or non-admin role redirects to /settings, never
// falls through to the GitHub surface. Relocates the inline
// GitHubConnectionCard that previously lived in SettingsPage.tsx into the
// CFG-IA cluster as a proper destination page.

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import GitHubConnectionCard from '../components/GitHubConnectionCard';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

export default function SettingsGitHubPage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const isAdmin = isAdminRole(myTenant?.role);

  // Fail CLOSED — a non-admin OR a null/failed membership redirects to
  // /settings rather than rendering the GitHub connection surface.
  useEffect(() => {
    if (!tenantLoading && !isAdmin) navigate('/settings');
  }, [tenantLoading, isAdmin, navigate]);

  // Fail-closed placeholder: non-admin / null membership is being redirected.
  if (!isAdmin) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
        <Sidebar />
        <main style={{ flex: 1, padding: 32 }}>
          <p data-testid="github-redirecting" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
            Redirecting…
          </p>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }} data-testid="github-page">
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/settings"
            style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
          >
            ← Back to settings
          </Link>
        </div>

        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          GitHub
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
          Connect your GitHub organisation and manage version-control credentials.
        </p>

        <GitHubConnectionCard isAdmin={isAdmin} />
      </main>
    </div>
  );
}
